// =============================================
// ESTADO DE CUENTA CONSOLIDADO
// Pagos y Gastos en orden cronológico
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  TableSortLabel,
  TablePagination,
} from '@mui/material';
import {
  GetApp as ExportIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import accountStatementService from '../../services/accountStatementService';
import userService from '../../services/userService';
import axios from 'axios';

const AccountStatement = () => {
  // Estados
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchYearsBack, setSearchYearsBack] = useState(3); // Default: 3 años
  const [summary, setSummary] = useState({
    initial_balance: 0,
    total_in: 0,
    total_out: 0,
    balance: 0,
    count: 0
  });

  // Filtros
  const [filters, setFilters] = useState({
    transaction_type: 'all',
    user_id: '',
    month: '',
    year: new Date().getFullYear(),
    start_date: '',
    end_date: '',
  });

  // Ordenamiento
  const [orderBy, setOrderBy] = useState('transaction_date');
  const [order, setOrder] = useState('desc');

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Cargar datos
  useEffect(() => {
    loadSystemParameters();
    loadData();
    loadUsers();
  }, []);

  // Aplicar filtros cuando cambien
  useEffect(() => {
    applyFiltersAndSort();
  }, [filters, transactions, orderBy, order]);

  const loadSystemParameters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/system-parameters`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const params = response.data.parameters || [];
      const searchYearsParam = params.find(p => p.param_key === 'search_years_back');

      if (searchYearsParam) {
        setSearchYearsBack(parseInt(searchYearsParam.param_value) || 3);
      }
    } catch (err) {
      console.error('Error al cargar parámetros del sistema:', err);
      // Mantener el valor por defecto de 3
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Construir parámetros de consulta según filtros
      const params = {};

      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.transaction_type !== 'all') params.transaction_type = filters.transaction_type;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;

      const response = await accountStatementService.getStatement(params);

      setTransactions(response.transactions || []);
      setSummary(response.summary || {});
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getAll();
      const owners = response.users?.filter(u => u.role === 'owner') || [];
      setUsers(owners);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...transactions];

    // NO aplicar filtros de año/mes a la fila de saldo inicial
    // Filtrar por tipo (excepto saldo_inicial)
    if (filters.transaction_type !== 'all') {
      filtered = filtered.filter(t =>
        t.transaction_type === 'saldo_inicial' || t.transaction_type === filters.transaction_type
      );
    }

    // Filtrar por propietario (saldo_inicial no tiene user_id)
    if (filters.user_id) {
      filtered = filtered.filter(t =>
        t.transaction_type === 'saldo_inicial' || t.user_id === parseInt(filters.user_id)
      );
    }

    // Filtrar por mes (saldo_inicial no tiene month)
    if (filters.month) {
      filtered = filtered.filter(t =>
        t.transaction_type === 'saldo_inicial' || t.month === parseInt(filters.month)
      );
    }

    // Filtrar por año (saldo_inicial no tiene year)
    if (filters.year) {
      filtered = filtered.filter(t =>
        t.transaction_type === 'saldo_inicial' || t.year === parseInt(filters.year)
      );
    }

    // Filtrar por rango de fechas
    if (filters.start_date) {
      filtered = filtered.filter(t => new Date(t.transaction_date) >= new Date(filters.start_date));
    }
    if (filters.end_date) {
      filtered = filtered.filter(t => new Date(t.transaction_date) <= new Date(filters.end_date));
    }

    // Ordenar
    filtered.sort((a, b) => {
      // Siempre mantener saldo_inicial al principio en orden ASC, al final en DESC
      if (a.transaction_type === 'saldo_inicial' && order === 'asc') return -1;
      if (b.transaction_type === 'saldo_inicial' && order === 'asc') return 1;
      if (a.transaction_type === 'saldo_inicial' && order === 'desc') return 1;
      if (b.transaction_type === 'saldo_inicial' && order === 'desc') return -1;

      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === 'transaction_date' || orderBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (orderBy === 'amount_in' || orderBy === 'amount_out' || orderBy === 'running_balance') {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue ? bValue.toLowerCase() : '';
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    // NO recalcular saldo acumulado - ya viene calculado del backend
    // Solo recalcular totales excluyendo el saldo inicial
    const transactionsWithoutInitial = filtered.filter(t => t.transaction_type !== 'saldo_inicial');
    const totalIn = transactionsWithoutInitial.reduce((sum, t) => sum + parseFloat(t.amount_in || 0), 0);
    const totalOut = transactionsWithoutInitial.reduce((sum, t) => sum + parseFloat(t.amount_out || 0), 0);

    setSummary(prev => ({
      ...prev,
      total_in: totalIn,
      total_out: totalOut,
      balance: totalIn - totalOut,
      count: transactionsWithoutInitial.length
    }));

    setFilteredTransactions(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
    setPage(0);
  };

  const handleApplyFilters = () => {
    loadData(); // Recargar con los filtros actuales
  };

  const handleClearFilters = () => {
    setFilters({
      transaction_type: 'all',
      user_id: '',
      month: '',
      year: new Date().getFullYear(),
      start_date: '',
      end_date: '',
    });
    // Recargar sin filtros
    setTimeout(() => loadData(), 100);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToPDF = () => {
    try {
      // Construir URL con filtros actuales
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.transaction_type !== 'all') params.append('transaction_type', filters.transaction_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.month) params.append('month', filters.month);
      if (filters.year) params.append('year', filters.year);

      // Obtener token de autenticación
      const token = localStorage.getItem('token');

      // Abrir PDF en nueva ventana
      const pdfUrl = `${process.env.REACT_APP_API_URL}/account-statement/pdf?${params.toString()}`;
      window.open(pdfUrl, '_blank');
    } catch (err) {
      setError('Error al exportar a PDF');
    }
  };

  const handleExportToExcel = async () => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      setError('No hay datos para exportar');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Estado de Cuenta');

      // Definir Columnas
      worksheet.columns = [
        { header: 'Fecha', key: 'date', width: 15 },
        { header: 'Tipo', key: 'type', width: 15 },
        { header: 'Propietario/Categoría', key: 'source', width: 35 },
        { header: 'Descripción', key: 'description', width: 45 },
        { header: 'Entrada', key: 'in', width: 15, style: { numFmt: '"B/."#,##0.00' } },
        { header: 'Salida', key: 'out', width: 15, style: { numFmt: '"B/."#,##0.00' } },
        { header: 'Saldo', key: 'balance', width: 15, style: { numFmt: '"B/."#,##0.00' } },
        { header: 'Mes', key: 'month', width: 12 },
        { header: 'Año', key: 'year', width: 10 }
      ];

      // Estilo de encabezado (Verde azulado oscuro #004d4d)
      const headerRow = worksheet.getRow(1);
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF004D4D' }
        };
        cell.font = {
          color: { argb: 'FFFFFFFF' },
          bold: true,
          size: 11
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Agregar datos
      filteredTransactions.forEach((t, index) => {
        const row = worksheet.addRow({
          date: formatDate(t.transaction_date),
          type: getTransactionTypeLabel(t.transaction_type),
          source: t.transaction_type === 'ingreso'
            ? `${t.user_name} - Casa ${t.house_number}`
            : t.transaction_type === 'saldo_inicial'
              ? 'Saldo Inicial'
              : t.category,
          description: t.description,
          in: t.amount_in > 0 ? parseFloat(t.amount_in) : null,
          out: t.amount_out > 0 ? parseFloat(t.amount_out) : null,
          balance: parseFloat(t.running_balance),
          month: t.month ? getMonthName(t.month) : '',
          year: t.year || ''
        });

        // Filas alternas y colores según tipo
        if (index % 2 === 0) {
          row.eachCell((cell) => {
            if (!cell.fill) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF9F9F9' }
              };
            }
          });
        }

        // Color especial para Saldo Inicial
        if (t.transaction_type === 'saldo_inicial') {
          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFE3F2FD' } // Azul muy claro
            };
            cell.font = { bold: true, color: { argb: 'FF1976D2' } };
          });
        }

        // Bordes completos y alineación (incluyendo celdas vacías)
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle' };
        });
      });

      // Agregar fila de totales
      const totalIn = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.amount_in || 0), 0);
      const totalOut = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.amount_out || 0), 0);

      const totalRow = worksheet.addRow({
        description: 'TOTALES',
        in: totalIn,
        out: totalOut,
        balance: totalIn - totalOut
      });

      totalRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0F2F1' } // Verde muy claro
        };
        cell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
      });

      // Congelar panel superior
      worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

      // Auto-filtros
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: 9 }
      };

      // Generar y descargar
      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `estado_cuenta_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(new Blob([buffer]), fileName);

    } catch (err) {
      console.error('Error al exportar a Excel:', err);
      setError('Error al exportar a Excel');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getMonthName = (month) => {
    if (!month) return '';
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[month - 1] || '';
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'ingreso': return 'Ingreso';
      case 'egreso': return 'Egreso';
      case 'saldo_inicial': return 'Saldo Inicial';
      default: return type;
    }
  };

  const getTransactionTypeChip = (type) => {
    switch (type) {
      case 'ingreso':
        return <Chip label="Ingreso" color="success" size="small" />;
      case 'egreso':
        return <Chip label="Egreso" color="error" size="small" />;
      case 'saldo_inicial':
        return <Chip label="Saldo Inicial" color="info" size="small" />;
      default:
        return <Chip label={type} size="small" />;
    }
  };

  // Transacciones paginadas
  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Estado de Cuenta
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={handleExportToPDF}
            size="small"
            disabled={loading || filteredTransactions.length === 0}
          >
            EXPORTAR PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExportToExcel}
            size="small"
            disabled={loading || filteredTransactions.length === 0}
          >
            EXPORTAR EXCEL
          </Button>
        </Box>
      </Box>

      {/* Resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IncomeIcon />
              <Typography variant="body2">Total Ingresos</Typography>
            </Box>
            <Typography variant="h5" fontWeight="bold">
              {formatCurrency(summary.total_in)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExpenseIcon />
              <Typography variant="body2">Total Egresos</Typography>
            </Box>
            <Typography variant="h5" fontWeight="bold">
              {formatCurrency(summary.total_out)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, bgcolor: 'warning.main', color: 'white' }}>
            <Typography variant="body2">Balance</Typography>
            <Typography variant="h5" fontWeight="bold">
              {formatCurrency(summary.period_balance || summary.balance)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">Transacciones</Typography>
            <Typography variant="h5" fontWeight="bold">
              {summary.count}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterIcon />
          <Typography variant="h6">Filtros</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            size="small"
          >
            LIMPIAR
          </Button>
        </Box>

        <Grid container spacing={2}>
          {/* Tipo */}
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filters.transaction_type}
                label="Tipo"
                onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="ingreso">Ingresos</MenuItem>
                <MenuItem value="egreso">Egresos</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Propietario */}
          <Grid item xs={12} sm={2.5}>
            <FormControl sx={{ width: 260 }}>
              <InputLabel>Propietario</InputLabel>
              <Select
                value={filters.user_id}
                label="Propietario"
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name} {user.house_number ? `- Casa ${user.house_number}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Mes */}
          <Grid item xs={12} sm={2}>
            <FormControl sx={{ width: 80 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                value={filters.month}
                label="Mes"
                onChange={(e) => handleFilterChange('month', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <MenuItem key={month} value={month}>
                    {getMonthName(month)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Año */}
          <Grid item xs={12} sm={2}>
            <FormControl sx={{ width: 90 }}>
              <InputLabel>Año</InputLabel>
              <Select
                value={filters.year}
                label="Año"
                onChange={(e) => handleFilterChange('year', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: searchYearsBack }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Fecha Inicio */}
          <Grid item xs={12} sm={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Desde"
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Fecha Fin */}
          <Grid item xs={12} sm={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Hasta"
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Botón Aplicar */}
          <Grid item xs={12} sm={0.5}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyFilters}
              sx={{ height: '40px' }}
            >
              Aplicar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'transaction_date'}
                      direction={orderBy === 'transaction_date' ? order : 'asc'}
                      onClick={() => handleRequestSort('transaction_date')}
                    >
                      Fecha
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Propietario/Categoría</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'amount_in'}
                      direction={orderBy === 'amount_in' ? order : 'asc'}
                      onClick={() => handleRequestSort('amount_in')}
                    >
                      Entrada
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'amount_out'}
                      direction={orderBy === 'amount_out' ? order : 'asc'}
                      onClick={() => handleRequestSort('amount_out')}
                    >
                      Salida
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Saldo</TableCell>
                  <TableCell>Mes</TableCell>
                  <TableCell>Año</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTransactions.map((transaction, index) => {
                  const isInitialBalance = transaction.transaction_type === 'saldo_inicial';

                  return (
                    <TableRow
                      key={transaction.transaction_id || `initial-${index}`}
                      hover
                      sx={{
                        bgcolor: isInitialBalance ? 'rgba(33, 150, 243, 0.15)' : 'inherit',
                        '& td': {
                          fontWeight: isInitialBalance ? 'bold' : 'normal',
                          color: isInitialBalance ? '#1976d2' : 'inherit'
                        }
                      }}
                    >
                      <TableCell>{formatDate(transaction.transaction_date)}</TableCell>
                      <TableCell>
                        {getTransactionTypeChip(transaction.transaction_type)}
                      </TableCell>
                      <TableCell>
                        {transaction.transaction_type === 'ingreso' ? (
                          <Box>
                            <Typography variant="body2">{transaction.user_name}</Typography>
                            {transaction.house_number && (
                              <Typography variant="caption" color="text.secondary">
                                Casa {transaction.house_number}
                              </Typography>
                            )}
                          </Box>
                        ) : transaction.transaction_type === 'saldo_inicial' ? (
                          <Chip label="Saldo Inicial" size="small" color="info" variant="outlined" />
                        ) : (
                          <Chip label={transaction.category} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={isInitialBalance ? 'bold' : 'normal'}>
                          {transaction.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {transaction.amount_in > 0 && (
                          <Typography color="success.main" fontWeight="bold">
                            {formatCurrency(transaction.amount_in)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.amount_out > 0 && (
                          <Typography color="error.main" fontWeight="bold">
                            {formatCurrency(transaction.amount_out)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color={isInitialBalance ? 'info.main' : 'inherit'}>
                          {formatCurrency(transaction.running_balance)}
                        </Typography>
                      </TableCell>
                      <TableCell>{transaction.month ? getMonthName(transaction.month) : '-'}</TableCell>
                      <TableCell>{transaction.year || '-'}</TableCell>
                    </TableRow>
                  );
                })}

                {/* Fila de totales */}
                {filteredTransactions.length > 0 && (
                  <TableRow sx={{ bgcolor: 'grey.100', '& td': { fontWeight: 'bold' } }}>
                    <TableCell colSpan={4} align="right">
                      TOTALES ({summary.count} transacciones):
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="success.main" variant="body1">
                        {formatCurrency(summary.total_in)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="error.main" variant="body1">
                        {formatCurrency(summary.total_out)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" variant="body1">
                        {formatCurrency(summary.balance)}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[12, 24, 48, 96]}
              component="div"
              count={filteredTransactions.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>

          {filteredTransactions.length === 0 && !loading && (
            <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No se encontraron transacciones con los filtros aplicados
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
};

export default AccountStatement;
